import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Copy, 
  Hash, 
  Youtube, 
  Twitch, 
  MessageSquare, 
  Shield,
  Key
} from "lucide-react";
import { toast } from "sonner";

interface TrackingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: {
    title: string;
    hashtag?: string;
    youtube_tag?: string;
    twitch_category?: string;
    keymailer_hashtag?: string;
    disclosure_text?: string;
  };
  gameKey?: string;
  onStartChat?: () => void;
  userType?: string; // Pour différencier développeurs et créateurs
}

export const TrackingInfoModal = ({ 
  isOpen, 
  onClose, 
  game, 
  gameKey,
  onStartChat,
  userType = 'creator'
}: TrackingInfoModalProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Vérification de sécurité pour éviter les erreurs null
  if (!game) {
    return null;
  }

  const trackingFields = [
    {
      label: "HASHTAG",
      value: game.hashtag || `#${game.title?.toLowerCase().replace(/\s+/g, '') || 'game'}`,
      description: "Use this hashtag on TikTok, Youtube, Instagram, and Facebook. It will also be recognized in stream titles and video descriptions",
      icon: <Hash className="w-4 h-4" />
    },
    {
      label: "TWITCH CATEGORY",
      value: game.twitch_category || game.title || 'Game',
      description: "Switch to this category during your Twitch stream",
      icon: <Twitch className="w-4 h-4" />
    },
    {
      label: "IndieKeys HASHTAG",
      value: game.keymailer_hashtag || "#indiekeys",
      description: "For having your coverage identified, it is recommended to add this hashtag to your content",
      icon: <Hash className="w-4 h-4" />
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
            {userType === 'developer' ? 'GIVEN KEY' : 'TRACKING YOUR COVERAGE'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {userType === 'developer' ? (
            /* Coverage Section for Developers */
            <div className="space-y-4">
              {/* <h3 className="text-lg font-semibold text-gray-700 mb-3">Video Coverage</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-custom-paddings">
                  <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    <Youtube className="w-6 h-6 text-gray-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">
                          No coverage yet
                        </h4>
                        <p className="text-xs text-gray-300 mt-1">
                          Creators will appear here once they create content for {game.title}
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-gray-400">0 views</span>
                          <span className="text-xs text-gray-400">0 likes</span>
                          <span className="text-xs text-gray-400">#pending</span>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs ml-2">
                        Pending
                      </Badge>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          ) : (
            /* Tracking Fields for Creators */
            <div className="space-y-4">
              {trackingFields.map((field, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-32 flex-shrink-0">
                    <label className="text-sm font-semibold text-gray-700">
                      {field.label}:
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 bg-gray-100 rounded-md px-3 py-2 font-mono text-sm text-gray-900">
                      {field.value}
                    </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(field.value, field.label)}
                        className="flex-shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {copiedField === field.label && (
                        <Badge variant="secondary" className="text-xs">
                          Copied!
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-600">
                      {field.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Disclosure - Only for Creators */}
          {userType !== 'developer' && (
            <div className="border-t pt-4">
              <div className="flex items-start gap-4">
                <div className="w-32 flex-shrink-0">
                  <label className="text-sm font-semibold text-gray-700">
                    DISCLOSURE:
                  </label>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="flex-1 bg-gray-100 rounded-md px-3 py-2 text-sm text-gray-900">
                      {game.disclosure_text || "Disclosure: I received a free review copy of this product from IndieKeys"}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(
                        game.disclosure_text || "Disclosure: I received a free review copy of this product from IndieKeys",
                        "DISCLOSURE"
                      )}
                      className="flex-shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600">
                    Many countries and platforms require creators to disclose paid content or gifts. 
                    This varies from country to country. You should be familiar with your local requirements. 
                    Our disclosure is an example and will cover many, but not all, disclosure requirements worldwide.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Game Key Section */}
          {gameKey && (
            <>
              <div className="border-t pt-4">
                <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-4">STEAM KEY</h3>
                <div className="flex items-start gap-4">
                  <div className="w-32 flex-shrink-0">
                    <label className="text-sm font-semibold text-gray-700">
                      YOUR KEY:
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 bg-gray-100 rounded-md px-3 py-2 font-mono text-sm text-gray-900">
                      {gameKey}
                    </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(gameKey, "GAME KEY")}
                        className="flex-shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600">
                      This key can only be activated on Steam
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Chat Button - Only for Creators */}
          {onStartChat && userType !== 'developer' && (
            <div className="border-t pt-4">
              <div className="flex justify-center">
                <Button
                  onClick={onStartChat}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 hover:from-purple-700 hover:via-pink-700 hover:to-blue-700 text-white"
                >
                  <MessageSquare className="w-4 h-4" />
                  Start Chat with Developer
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
